import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Row, Col, Card, Button, Typography, Space, Tag, Spin, notification,
  Select, Input, Table, Modal, Divider, Radio, Badge, Alert, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined, SendOutlined, TeamOutlined, FilterOutlined,
  FilePdfOutlined, UserOutlined, ReloadOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, ClockCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import {
  readReport, filterCount, sendAll, sendSelected, listMembers,
} from './monthlyReportService';

const { Title, Text } = Typography;
const { Option } = Select;

const SEND_MODES = [
  { value: 'all',      label: 'Send to All (Filtered)' },
  { value: 'selected', label: 'Send to Selected Members' },
];

const DEFAULT_FILTERS = {
  status:       'active',
  address_type: 'permanent',
  village:      '',
  tehsil:       '',
  district:     '',
  phone:        '',
};

export default function SendMonthlyReport() {
  const history = useHistory();
  const { id }  = useParams();

  const [report, setReport]       = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);

  const [sendMode, setSendMode]   = useState('all');
  const [filters, setFilters]     = useState({ ...DEFAULT_FILTERS });
  const [skipSent, setSkipSent]   = useState(true);

  const [counting, setCounting]   = useState(false);
  const [count, setCount]         = useState(null);

  // Selected members table
  const [members, setMembers]         = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination]   = useState({ current: 1, pageSize: 20, total: 0 });

  const [sending, setSending]     = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  useEffect(() => { loadReport(); }, []); // eslint-disable-line

  const loadReport = async () => {
    setLoadingReport(true);
    try {
      const res = await readReport(id);
      if (res.status) setReport(res.result);
      else notification.error({ message: 'Report not found' });
    } catch (err) {
      notification.error({ message: err.message });
    } finally {
      setLoadingReport(false);
    }
  };

  const handleCount = async () => {
    setCounting(true);
    setCount(null);
    try {
      const res = await filterCount({ ...filters, skip_sent: skipSent, report_id: parseInt(id) });
      if (res.status) setCount(res.total);
      else notification.warning({ message: res.message });
    } catch (err) {
      notification.error({ message: 'Count failed', description: err.message });
    } finally {
      setCounting(false);
    }
  };

  const fetchMembers = useCallback(async (page = 1, pageSize = 20) => {
    setMembersLoading(true);
    try {
      const params = { page, limit: pageSize, ...filters };
      const res = await listMembers(params);
      if (res.status) {
        setMembers(res.result.map((r, i) => ({ ...r, key: r.id, sno: (page - 1) * pageSize + i + 1 })));
        setPagination({ current: page, pageSize, total: res.pagination?.total || 0 });
      }
    } catch (err) {
      notification.error({ message: 'Failed to load members', description: err.message });
    } finally {
      setMembersLoading(false);
    }
  }, [filters]); // eslint-disable-line

  useEffect(() => {
    if (sendMode === 'selected') fetchMembers();
  }, [sendMode]); // eslint-disable-line

  const handleSend = async () => {
    setSending(true);
    setConfirmVisible(false);
    try {
      let res;
      if (sendMode === 'all') {
        res = await sendAll(id, { ...filters, skip_sent: skipSent });
      } else {
        if (!selectedRowKeys.length) {
          notification.warning({ message: 'Select at least one member' });
          setSending(false);
          return;
        }
        res = await sendSelected(id, selectedRowKeys);
      }

      if (res.status) {
        notification.success({
          message:     'Report dispatched!',
          description: res.message,
          duration:    8,
        });
        history.push('/monthly-reports');
      } else {
        notification.warning({ message: res.message });
      }
    } catch (err) {
      notification.error({ message: 'Send failed', description: err.message });
    } finally {
      setSending(false);
    }
  };

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCount(null);
  };

  const resetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setCount(null);
    setSelectedRowKeys([]);
  };

  const activeFilters = Object.entries(filters).filter(([k, v]) => v && v !== 'active' && v !== 'permanent');

  const memberColumns = [
    { title: '#', dataIndex: 'sno', width: 55, align: 'center' },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Phone', dataIndex: 'phone_no', width: 130 },
    {
      title: 'Village', dataIndex: 'per_village',
      render: (v, r) => v || r.tem_village || '-',
    },
    { title: 'District', dataIndex: 'per_district', width: 130 },
    {
      title: 'Status', dataIndex: 'member_status', width: 90,
      render: (v) => <Tag color={v === 'Active' ? 'green' : 'default'}>{v}</Tag>,
    },
  ];

  if (loadingReport) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  if (!report) {
    return (
      <div className="layout-content">
        <Alert type="error" message="Report not found" showIcon />
      </div>
    );
  }

  const [yr, mo] = (report.report_month || '').split('-');
  const months   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthLabel = yr ? `${months[parseInt(mo) - 1]} ${yr}` : report.report_month;

  const alreadySent  = report.send_status === 'sent' && report.last_sent_at;
  const lastSentLabel = alreadySent
    ? new Date(report.last_sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="layout-content">
      <Row gutter={[16, 16]}>
        {/* ── Report Info ── */}
        <Col xs={24}>
          <Card
            title={
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/monthly-reports')} />
                <Title level={4} style={{ margin: 0, color: '#fff' }}>Send Monthly Report</Title>
              </Space>
            }
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
          >
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} sm={16}>
                <Space size={16} align="start">
                  <FilePdfOutlined style={{ fontSize: 32, color: '#ff4d4f', flexShrink: 0 }} />
                  <div>
                    <Text strong style={{ fontSize: 16, display: 'block' }}>{report.title}</Text>
                    <Text type="secondary">{monthLabel} · Template: <b>{report.template_name}</b></Text>
                  </div>
                </Space>
              </Col>
              <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                {report.file_url ? (
                  <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: '4px 10px' }}>PDF Ready</Tag>
                ) : (
                  <Tag color="error" style={{ fontSize: 13, padding: '4px 10px' }}>No PDF — upload first</Tag>
                )}
              </Col>
            </Row>

            {alreadySent && (
              <Alert
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                style={{ marginTop: 14 }}
                message={
                  <Space>
                    <ClockCircleOutlined />
                    <Text strong>Already sent on {lastSentLabel}</Text>
                  </Space>
                }
                description="This report was already dispatched. Sending again will only reach members not yet sent (if 'Skip already sent' is ON), or all members (if OFF)."
              />
            )}

            {!report.file_url && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="Upload the PDF first before sending"
                action={
                  <Button size="small" onClick={() => history.push(`/monthly-reports/edit/${id}`)}>
                    Upload PDF
                  </Button>
                }
              />
            )}
          </Card>
        </Col>

        {/* ── Send Mode ── */}
        <Col xs={24} lg={8}>
          <Card title={<Space><SendOutlined /><span>Send Mode</span></Space>} size="small">
            <Radio.Group
              value={sendMode}
              onChange={(e) => { setSendMode(e.target.value); setCount(null); setSelectedRowKeys([]); }}
              buttonStyle="solid"
              style={{ width: '100%' }}
            >
              {SEND_MODES.map(m => (
                <Radio.Button key={m.value} value={m.value} style={{ width: '50%', textAlign: 'center', fontSize: 12 }}>
                  {m.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Card>

          {/* ── Filters ── */}
          <Card
            title={<Space><FilterOutlined /><span>Filters</span></Space>}
            size="small"
            style={{ marginTop: 16 }}
            extra={<Button size="small" onClick={resetFilters}>Reset</Button>}
          >
            <Row gutter={[12, 8]}>
              <Col xs={24} sm={12} lg={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>Member Status</Text>
                <Select value={filters.status} onChange={v => setFilter('status', v)} style={{ width: '100%' }} size="small">
                  <Option value="active">Active Only</Option>
                  <Option value="inactive">Inactive Only</Option>
                  <Option value="both">All Members</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>Address Type</Text>
                <Select value={filters.address_type} onChange={v => setFilter('address_type', v)} style={{ width: '100%' }} size="small">
                  <Option value="permanent">Permanent Address</Option>
                  <Option value="temporary">Temporary Address</Option>
                  <Option value="both">Both</Option>
                </Select>
              </Col>
              <Col xs={24} sm={8} lg={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>Village</Text>
                <Input
                  size="small"
                  placeholder="Filter by village..."
                  value={filters.village}
                  onChange={e => setFilter('village', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={8} lg={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>District</Text>
                <Input
                  size="small"
                  placeholder="Filter by district..."
                  value={filters.district}
                  onChange={e => setFilter('district', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={8} lg={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>Tehsil</Text>
                <Input
                  size="small"
                  placeholder="Filter by tehsil..."
                  value={filters.tehsil}
                  onChange={e => setFilter('tehsil', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={8} lg={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>Phone Number</Text>
                <Input
                  size="small"
                  placeholder="Search by phone..."
                  value={filters.phone}
                  onChange={e => setFilter('phone', e.target.value)}
                  maxLength={15}
                />
              </Col>
            </Row>

            <Divider style={{ margin: '10px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <Text style={{ fontSize: 12 }}>Skip already sent</Text>
              <Radio.Group
                value={skipSent ? 'yes' : 'no'}
                onChange={e => { setSkipSent(e.target.value === 'yes'); setCount(null); }}
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="yes">Yes</Radio.Button>
                <Radio.Button value="no">No</Radio.Button>
              </Radio.Group>
            </div>

            <Button
              block
              icon={<TeamOutlined />}
              loading={counting}
              onClick={handleCount}
            >
              Count Matching Members
            </Button>

            {count !== null && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Badge
                  count={count}
                  showZero
                  style={{ background: count > 0 ? '#52c41a' : '#d9d9d9', fontSize: 14, padding: '0 10px', height: 28, lineHeight: '28px' }}
                />
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                  {count} members will receive the report
                </Text>
              </div>
            )}

            {activeFilters.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Active filters: </Text>
                {activeFilters.map(([k, v]) => (
                  <Tag key={k} color="blue" style={{ fontSize: 11, margin: '2px' }}>{k}: {v}</Tag>
                ))}
              </div>
            )}
          </Card>

          {/* ── Send Button ── */}
          <Card size="small" style={{ marginTop: 16 }}>
            <Tooltip title={!report.file_url ? 'Upload PDF first' : ''}>
              <Button
                type="primary"
                block
                size="large"
                icon={<SendOutlined />}
                loading={sending}
                disabled={!report.file_url || sending}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => setConfirmVisible(true)}
              >
                {sendMode === 'all'
                  ? `Send to ${count !== null ? count : 'All Matching'} Members`
                  : `Send to ${selectedRowKeys.length} Selected`}
              </Button>
            </Tooltip>
          </Card>
        </Col>

        {/* ── Members Table (for selected mode) ── */}
        {sendMode === 'selected' && (
          <Col xs={24} lg={16}>
            <Card
              title={<Space><UserOutlined /><span>Select Members</span></Space>}
              size="small"
              extra={
                <Space>
                  <Badge count={selectedRowKeys.length} style={{ background: '#7a0000' }}>
                    <Text style={{ fontSize: 12 }}>Selected</Text>
                  </Badge>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchMembers(1, pagination.pageSize)}
                  >Apply Filters</Button>
                </Space>
              }
            >
              <Table
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys, preserveSelectedRowKeys: true }}
                columns={memberColumns}
                dataSource={members}
                loading={membersLoading}
                rowKey="id"
                size="small"
                scroll={{ x: 600 }}
                pagination={{
                  current:       pagination.current,
                  pageSize:      pagination.pageSize,
                  total:         pagination.total,
                  showSizeChanger: true,
                  showTotal:     (t) => `${t} members`,
                  onChange:      (page, pageSize) => fetchMembers(page, pageSize),
                }}
              />
            </Card>
          </Col>
        )}
      </Row>

      {/* ── Confirm Modal ── */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Confirm Send</span>
          </Space>
        }
        open={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onOk={handleSend}
        okType="danger"
        okText="Yes, Send Now"
        confirmLoading={sending}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Report: </Text>
            <Text>{report.title}</Text>
          </div>
          <div>
            <Text strong>Mode: </Text>
            <Tag color="blue">{sendMode === 'all' ? 'Send to All Filtered' : `Send to ${selectedRowKeys.length} Selected`}</Tag>
          </div>
          {count !== null && sendMode === 'all' && (
            <div>
              <Text strong>Recipients: </Text>
              <Tag color="green">{count} members</Tag>
            </div>
          )}
          <div>
            <Text strong>Template: </Text>
            <Text code>{report.template_name}</Text>
          </div>
          <div>
            <Text strong>Skip already sent: </Text>
            <Tag color={skipSent ? 'green' : 'orange'}>{skipSent ? 'Yes' : 'No'}</Tag>
          </div>
          <Alert
            type="warning"
            showIcon
            message="This will send WhatsApp messages to all matching members and cannot be undone."
            style={{ marginTop: 8 }}
          />
        </Space>
      </Modal>
    </div>
  );
}
